<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import FilterPill from 'dashboard/components-next/filter-bar/FilterPill.vue';
import {
  PILL_OPERATORS,
  operatorHasValue,
  pillKind,
  pillOperators,
} from './pillsPayload';

// UI-FILTROS-01 (D3, PLAN §2.3): uma condição do filtro avançado numa pílula. A primeira linha
// escolhe o operador (#operator); abaixo vêm as opções (lista, etiqueta, sim/não) ou o campo
// (texto, número, data). O valor é a pílula do pillsPayload, ou null quando não filtra.
const props = defineProps({
  // O atributo no formato do provider (provider.js, contactProvider.js).
  filterType: { type: Object, required: true },
  // Só leitura: a visão com OU entre atributos (D6).
  disabled: { type: Boolean, default: false },
  disabledHint: { type: String, default: '' },
});

const emit = defineEmits(['clear']);

const pill = defineModel({ type: Object, default: null });

const { t, locale } = useI18n();

const kind = computed(() => pillKind(props.filterType));
const operadores = computed(() => pillOperators(props.filterType));
const operadorPadrao = () => operadores.value[0];

const operador = ref(pill.value?.filterOperator ?? operadorPadrao());
// O que se digita no campo (texto, número, data) antes de aplicar; `ate` é o fim do "entre".
const valor = ref('');
const ate = ref('');

const lerRascunho = () => {
  const [inicio = '', fim = ''] = pill.value?.values || [];
  valor.value = String(inicio);
  ate.value = String(fim);
};

// A última pílula que saiu daqui. Quando ela volta pelo v-model, não é mudança de fora: o
// operador escolhido sem valor ainda (ex.: "entre" antes das datas) tem de ficar.
let emitida;
const emitir = novo => {
  emitida = novo;
  pill.value = novo;
};

watch(
  pill,
  novo => {
    if (novo === emitida) return;
    operador.value = novo?.filterOperator ?? operadorPadrao();
    lerRascunho();
  },
  { immediate: true }
);

const opcoes = computed(() => {
  if (kind.value === 'boolean') {
    return [
      { id: true, name: t('FILTER_BAR.CONDITION.YES') },
      { id: false, name: t('FILTER_BAR.CONDITION.NO') },
    ];
  }
  return (props.filterType.options || []).map(({ id, name, title, icon }) => ({
    id,
    name: name ?? title ?? String(id),
    icon,
  }));
});

const temValor = computed(() => operatorHasValue(operador.value));
// Lista, etiqueta e sim/não escolhem nas opções; o resto usa o campo do #body.
const usaLista = computed(
  () => ['list', 'labels', 'boolean'].includes(kind.value) && temValor.value
);

const montar = (filterOperator, values) => ({
  attributeKey: props.filterType.attributeKey,
  filterOperator,
  values,
  attributeModel: pill.value?.attributeModel ?? props.filterType.attributeModel,
  customAttributeType: pill.value?.customAttributeType ?? '',
});

const valoresDaLista = computed(() =>
  usaLista.value && pill.value?.filterOperator === operador.value
    ? pill.value.values
    : []
);

const mudarLista = valores => {
  if (!usaLista.value) return;
  emitir(valores.length ? montar(operador.value, valores) : null);
};

// Os operadores que usam o mesmo valor: trocar entre eles mantém o que já foi escolhido.
const grupo = op => {
  if (op === PILL_OPERATORS.BETWEEN || op === 'days_before') return op;
  return operatorHasValue(op) ? 'um' : 'nenhum';
};

const escolherOperador = novo => {
  if (props.disabled || novo === operador.value) return;
  const anterior = operador.value;
  operador.value = novo;
  if (!operatorHasValue(novo)) {
    emitir(montar(novo, []));
    return;
  }
  const mesmoValor = grupo(anterior) === grupo(novo);
  if (pill.value && mesmoValor && pill.value.values?.length) {
    emitir(montar(novo, pill.value.values));
    return;
  }
  if (pill.value) emitir(null);
  if (!mesmoValor) {
    valor.value = '';
    ate.value = '';
  }
};

const limpar = () => {
  emitir(null);
  operador.value = operadorPadrao();
  valor.value = '';
  ate.value = '';
  emit('clear');
};

const tipoDoCampo = computed(() => {
  if (operador.value === 'days_before') return 'number';
  if (kind.value === 'date') return 'date';
  if (kind.value === 'number') return 'number';
  return 'text';
});

const valoresDoCampo = computed(() => {
  const inicio = String(valor.value).trim();
  if (operador.value === PILL_OPERATORS.BETWEEN) {
    const fim = String(ate.value).trim();
    return inicio && fim ? [inicio, fim] : [];
  }
  return inicio ? [inicio] : [];
});

const aplicar = close => {
  if (!valoresDoCampo.value.length) return;
  emitir(montar(operador.value, valoresDoCampo.value));
  close();
};

const nomeDoOperador = op => {
  if (op === PILL_OPERATORS.BETWEEN) return t('FILTER_BAR.CONDITION.BETWEEN');
  if (op === PILL_OPERATORS.HAS_ALL) return t('FILTER_BAR.CONDITION.HAS_ALL');
  // Uma pasta antiga pode trazer o que o atributo não oferece mais ("Identificador da conversa:
  // contém 12"), e a pílula só de leitura mostra o nome do operador, não a chave crua.
  return (
    props.filterType.filterOperators?.find(({ value }) => value === op)
      ?.label ?? t(`FILTER.OPERATOR_LABELS.${op}`)
  );
};

const formatarDia = dia => {
  const [ano, mes, numero] = String(dia).split('-').map(Number);
  if (!ano || !mes || !numero) return String(dia);
  return new Intl.DateTimeFormat(String(locale.value).replace('_', '-'), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(ano, mes - 1, numero));
};

const textoDosValores = computed(() => {
  const valores = pill.value?.values || [];
  if (['list', 'labels', 'boolean'].includes(kind.value)) {
    const nomes = valores.map(
      id => opcoes.value.find(opcao => opcao.id === id)?.name ?? String(id)
    );
    return nomes.length > 1 ? `${nomes[0]} +${nomes.length - 1}` : nomes[0];
  }
  if (pill.value?.filterOperator === PILL_OPERATORS.BETWEEN) {
    return t('FILTER_BAR.CONDITION.RANGE', {
      from: formatarDia(valores[0]),
      to: formatarDia(valores[1]),
    });
  }
  if (kind.value === 'date' && pill.value?.filterOperator !== 'days_before') {
    return formatarDia(valores[0]);
  }
  // Os valores vão todos: a pasta pode trazer mais de um ("é Ouro, Prata", que o backend casa
  // como IN), e o atributo que a conta não tem mais cai aqui como texto.
  return valores.map(String).join(', ');
});

// "Caixa: Bella +1" no "é"; nos outros o operador vai junto: "Caixa: Diferente Bella".
const resumo = computed(() => {
  if (!pill.value) return '';
  const op = pill.value.filterOperator;
  if (!operatorHasValue(op)) return nomeDoOperador(op);
  if (op === 'equal_to' || op === PILL_OPERATORS.BETWEEN) {
    return textoDosValores.value;
  }
  return t('FILTER_BAR.CONDITION.SUMMARY', {
    operator: nomeDoOperador(op),
    value: textoDosValores.value,
  });
});

const campo =
  'w-full h-8 min-w-0 px-3 text-sm rounded-lg outline-none reset-base bg-n-alpha-black2 text-n-slate-12 placeholder:text-n-slate-10 focus:ring-1 focus:ring-n-brand';
</script>

<template>
  <FilterPill
    :label="filterType.attributeName || filterType.label"
    :options="usaLista ? opcoes : []"
    :single="kind === 'boolean'"
    :summary="resumo"
    :active="Boolean(pill)"
    :disabled="disabled"
    :disabled-hint="disabledHint"
    :model-value="valoresDaLista"
    @update:model-value="mudarLista"
    @clear="limpar"
  >
    <template v-if="operadores.length > 1" #operator>
      <div
        role="radiogroup"
        class="flex flex-wrap gap-1"
        :aria-label="t('FILTER_BAR.CONDITION.OPERATOR')"
      >
        <button
          v-for="op in operadores"
          :key="op"
          type="button"
          role="radio"
          :aria-checked="op === operador"
          class="h-7 px-2 text-xs rounded-md whitespace-nowrap"
          :class="
            op === operador
              ? 'bg-pz-brand-tint text-pz-brand-on-tint font-medium'
              : 'text-n-slate-11 hover:bg-n-alpha-1 hover:text-n-slate-12'
          "
          @click="escolherOperador(op)"
        >
          {{ nomeDoOperador(op) }}
        </button>
      </div>
    </template>
    <template v-if="!usaLista" #body="{ close }">
      <form
        v-if="temValor"
        class="flex flex-col gap-2 px-2 pb-2"
        @submit.prevent="aplicar(close)"
      >
        <div class="flex gap-2">
          <input
            v-model="valor"
            :type="tipoDoCampo"
            :min="operador === 'days_before' ? 1 : undefined"
            :class="campo"
            :placeholder="
              operador === 'days_before'
                ? t('FILTER_BAR.CONDITION.DAYS')
                : t('FILTER_BAR.CONDITION.VALUE')
            "
            :aria-label="
              operador === PILL_OPERATORS.BETWEEN
                ? t('FILTER_BAR.CONDITION.FROM')
                : t('FILTER_BAR.CONDITION.VALUE')
            "
          />
          <input
            v-if="operador === PILL_OPERATORS.BETWEEN"
            v-model="ate"
            type="date"
            :class="campo"
            :aria-label="t('FILTER_BAR.CONDITION.TO')"
          />
        </div>
        <Button
          type="submit"
          size="sm"
          :label="t('FILTER_BAR.CONDITION.APPLY')"
          :disabled="!valoresDoCampo.length"
        />
      </form>
    </template>
  </FilterPill>
</template>
